import styled from 'styled-components';
import {
  BACKGROUND_COLOR,
  PRIMARY_COLOR,
  PRIMARY_TEXT_COLOR,
  BORDER_COLOR,
} from '../../../constants';
import { CloseButtonInModal } from '../CloseButtonInModal';
import TransitioningModal from '../../Modals/TransitioningModal';
import { StyledButton } from '../StyledButton';
import { AiOutlineCopy } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { DiscordLogo, RedditLogo } from './SocialLogos';

interface CustomerSupportModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

const CustomerSupportModal = ({ show, setShow }: CustomerSupportModalProps) => {
  const handleClose = () => setShow(false);

  const email = 'Hello@TheUncensored.AI';

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(email);
    toast.success('Email copied! Shoot us a message anytime 🥳');
  };

  return (
    <StyledModal
      isOpen={show}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 100,
        },
        content: {
          position: 'relative',
          backgroundColor: BACKGROUND_COLOR,
          padding: '16px',
          width: '90vw',
          maxWidth: '600px',
          maxHeight: '93vh',
          margin: 'auto',
          marginTop: window.innerWidth < 768 ? '2vh' : '3vh',
          overflow: 'hidden',
        },
      }}
      ariaHideApp={false}
      shouldFocusAfterRender={false}
      onRequestClose={handleClose}
    >
      <CloseButtonInModal onClick={handleClose} />

      <ModalContent>
        <SupportSection>
          <SupportItem>
            <SupportTitle>📧 Need help?</SupportTitle>
            <SupportContent>
              Chat with our CEO directly:
              <EmailContainer onClick={handleCopyEmail}>
                <EmailText>{email}</EmailText>
                <AiOutlineCopy size={24} color={PRIMARY_COLOR} />
              </EmailContainer>
            </SupportContent>
          </SupportItem>

          <SupportItem>
            <SupportTitle>👥 Ask from the Community</SupportTitle>
            <SupportContent>
              Join our community for tips and tricks:
              <SocialButtonsContainer>
                <DiscordLogo bigger />
                <RedditLogo bigger />
              </SocialButtonsContainer>
            </SupportContent>
          </SupportItem>

          <SupportItem>
            <SupportTitle>❓ Quick Fixes</SupportTitle>
            <SupportContent>
              <FAQItem>
                <FAQQuestion>Images not quite right?</FAQQuestion>
                <FAQAnswer>
                  No worries! Just hit generate again. Sometimes the AI needs a
                  couple tries to nail it. Alternatively, try tweaking the
                  prompt.
                </FAQAnswer>
              </FAQItem>

              <FAQItem>
                <FAQQuestion>Need refunds?</FAQQuestion>
                <FAQAnswer>
                  Just shoot us an email at {email} and we'll sort it out!
                </FAQAnswer>
              </FAQItem>

              <FAQItem>
                <FAQQuestion>Chat being weird?</FAQQuestion>
                <FAQAnswer>
                  Try starting a new chat session. Our AIs are still learning
                  and might get a bit confused.
                </FAQAnswer>
              </FAQItem>

              <FAQItem>
                <FAQQuestion>Taking forever to generate?</FAQQuestion>
                <FAQAnswer>
                  Our servers might be a bit busy. We're on it! Grab a drink and
                  check back in a minute. Sorry!
                </FAQAnswer>
              </FAQItem>
            </SupportContent>
          </SupportItem>
        </SupportSection>

        <ButtonContainer>
          <StyledButton onClick={handleClose}>Cool, thanks!</StyledButton>
        </ButtonContainer>
      </ModalContent>
    </StyledModal>
  );
};

const StyledModal = styled(TransitioningModal)`
  display: flex;
  flex-direction: column;
  z-index: 3;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: calc(90vh - 32px);
  padding-right: 8px;
  margin: 0 -8px 0 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
  }
`;

const SupportSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 12px 0;
`;

const SupportItem = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const SupportTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 18px;
  color: ${PRIMARY_COLOR};
`;

const SupportContent = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
`;

const EmailContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: 1px solid ${BORDER_COLOR};
  border-radius: 8px;
  background-color: transparent;
  cursor: pointer;
  margin-top: 8px;
  max-width: fit-content;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: ${PRIMARY_COLOR};
  }
`;

const EmailText = styled.span`
  font-size: 16px;
  color: ${PRIMARY_TEXT_COLOR};
  margin-right: 8px;
`;

const SocialButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const FAQItem = styled.div`
  margin-bottom: 16px;
  transition: transform 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    transform: translateX(2px);
  }
`;

const FAQQuestion = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.95);
`;

const FAQAnswer = styled.div`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

export default CustomerSupportModal;
